import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { ProgressBar } from 'primereact/progressbar';
import { Avatar } from 'primereact/avatar';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Chart } from 'primereact/chart';

import DialogHeader from '../components/ui/DialogHeader';

import { useAuth } from '../context/AuthContext';

export const LocalView = () => {
    const { user, axiosInstance } = useAuth();
    const toast = useRef(null);

    const [loading, setLoading] = useState(false);
    const [local, setLocal] = useState(null);
    const [mesasDialog, setMesasDialog] = useState(false);
    const [selectedMesa, setSelectedMesa] = useState(null);
    const [detalleMesaDialog, setDetalleMesaDialog] = useState(false);

    // Lista de candidatos con la ruta de su logo / símbolo oficial
    const CANDIDATOS_LISTA = [
        { codigo: 'APP', nombre: 'Junior Aguirre Álvarez', logo: '/logos/app.png' },
        { codigo: 'PL', nombre: 'Aristóteles Carlos Acosta', logo: '/logos/pl.jpg' },
        { codigo: 'AP', nombre: 'César Reyna Pérez', logo: '/logos/ap.png' },
        { codigo: 'RP', nombre: 'Rodolfo Aguilar Arteaga', logo: '/logos/rp.svg' },
        { codigo: 'FP', nombre: 'Roxana Cipra Reyes', logo: '/logos/fp.png' },
        { codigo: 'SP', nombre: 'Álvaro Sánchez Anticona', logo: '/logos/sp.svg' },
        { codigo: 'APRA', nombre: 'Ana Vásquez Aguilar', logo: '/logos/apra.png' },
        { codigo: 'PP', nombre: 'Sandra Guevara Villalobos', logo: '/logos/pp.png' },
        { codigo: 'OTROS', nombre: 'Otros', logo: '/logos/otros.png' }
    ];

    const [registroDialog, setRegistroDialog] = useState(false);
    const [mesaEnProceso, setMesaEnProceso] = useState(null);
    const [guardandoVotos, setGuardandoVotos] = useState(false);

    const emptyForm = {
        codigoMesa: '',
        personeroDocIdentidad: '',
        personeroNombre: '',
        personeroApellido: '',
        votosInvalidos: 0,
        noAsistieron: 0,
        votosObservados: 0,
        votosPorCandidato: CANDIDATOS_LISTA.reduce((acc, c) => ({ ...acc, [c.codigo]: 0 }), {})
    };

    const [formVotos, setFormVotos] = useState(emptyForm);

    const peticionGetLocalAsignado = useCallback(async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/locales/responsable/${user.id}`);
            setLocal(response.data);
        } catch (err) {
            console.error("Error al cargar local asignado:", err);
            if (err.response?.status === 404) {
                setLocal(null);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: err.response?.data?.message || 'No se pudo cargar la información del local.',
                    life: 3500
                });
            }
        } finally {
            setLoading(false);
        }
    }, [user?.id, axiosInstance]);

    useEffect(() => {
        peticionGetLocalAsignado();
    }, [peticionGetLocalAsignado]);

    const buscarPersoneroPorDNI = async () => {
        const dni = formVotos.personeroDocIdentidad?.trim();
        if (!dni || dni.length < 8) return;

        try {
            const res = await axiosInstance.get(`/persona/doc/${dni}`);
            if (res.data) {
                setFormVotos(prev => ({
                    ...prev,
                    personeroNombre: res.data.nombre || '',
                    personeroApellido: res.data.apellido || ''
                }));
                toast.current?.show({ severity: 'info', summary: 'Personero', detail: 'Datos autocompletados desde el padrón.', life: 2500 });
            }
        } catch (err) {
            // Permite ingreso manual si no existe en base de datos
        }
    };

    const abrirRegistroMesa = (mesa) => {
        setMesaEnProceso(mesa);
        setFormVotos({
            ...emptyForm,
            codigoMesa: mesa.codigo || ''
        });
        setRegistroDialog(true);
    };

    const limiteElectores = local?.electoresHabiles || 0;
    const totalValidos = Object.values(formVotos.votosPorCandidato).reduce((a, b) => (Number(a) || 0) + (Number(b) || 0), 0);
    const totalGeneral = totalValidos + (Number(formVotos.votosInvalidos) || 0) + (Number(formVotos.noAsistieron) || 0) + (Number(formVotos.votosObservados) || 0);

    const guardarEscrutinio = async () => {
        if (!formVotos.codigoMesa?.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Campo requerido', detail: 'Debe ingresar el código oficial de la mesa.' });
            return;
        }

        if (!formVotos.personeroDocIdentidad?.trim() || !formVotos.personeroNombre?.trim() || !formVotos.personeroApellido?.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Campos requeridos', detail: 'Debe ingresar el DNI y nombre completo del personero.' });
            return;
        }

        if (limiteElectores > 0 && totalGeneral > limiteElectores) {
            toast.current?.show({
                severity: 'error',
                summary: 'Límite Superado',
                detail: `El cómputo (${totalGeneral}) excede el tope de votantes del local (${limiteElectores}).`
            });
            return;
        }

        setGuardandoVotos(true);
        try {
            const payload = {
                ...formVotos,
                votosValidos: totalValidos
            };

            await axiosInstance.post(`/mesas/${mesaEnProceso.id}/registrar-votos`, payload);
            toast.current?.show({ severity: 'success', summary: 'Acta Registrada', detail: `Mesa N° ${mesaEnProceso.numeroMesa} computada correctamente.` });

            setRegistroDialog(false);
            await peticionGetLocalAsignado();
        } catch (err) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: err.response?.data?.message || 'No se pudo guardar el escrutinio.'
            });
        } finally {
            setGuardandoVotos(false);
        }
    };

    const mesasRegistradasCount = local?.mesas?.filter(m => m.registrada).length || 0;
    const porcentajeAvance = local?.cantidadMesas > 0
        ? Math.round((mesasRegistradasCount / local.cantidadMesas) * 100)
        : 0;

    const estadoMesaTemplate = (rowData) => {
        return (
            <Tag
                value={rowData.registrada ? 'PROCESADA' : 'PENDIENTE'}
                severity={rowData.registrada ? 'success' : 'warning'}
                className="px-2 py-1 text-xs font-semibold uppercase"
                icon={rowData.registrada ? 'pi pi-check-circle' : 'pi pi-clock'}
            />
        );
    };

    const pieChartData = React.useMemo(() => {
        if (!local?.mesas || local.mesas.length === 0) return null;

        const conteoCandidatos = {};
        let totalInvalidos = 0;
        let totalObservados = 0;
        let totalAusentes = 0;

        local.mesas.forEach((mesa) => {
            if (mesa.registrada) {
                totalInvalidos += (mesa.votosInvalidos || 0);
                totalObservados += (mesa.votosObservados || 0);
                totalAusentes += (mesa.noAsistieron || 0);

                if (mesa.votosCandidatos) {
                    mesa.votosCandidatos.forEach((vc) => {
                        const nombre = vc.candidato || 'Sin Nombre';
                        conteoCandidatos[nombre] = (conteoCandidatos[nombre] || 0) + (vc.votos || 0);
                    });
                }
            }
        });

        const candidatosOrdenados = Object.entries(conteoCandidatos)
            .map(([nombre, votos]) => ({ nombre, votos }))
            .sort((a, b) => b.votos - a.votos);

        const top4 = candidatosOrdenados.slice(0, 4);
        const restoVotos = candidatosOrdenados.slice(4).reduce((acc, c) => acc + c.votos, 0);

        const labels = [];
        const dataValues = [];

        top4.forEach((c) => {
            labels.push(c.nombre);
            dataValues.push(c.votos);
        });

        if (restoVotos > 0) {
            labels.push('Otros Candidatos');
            dataValues.push(restoVotos);
        }

        if (totalInvalidos > 0) {
            labels.push('Inválidos / Nulos');
            dataValues.push(totalInvalidos);
        }

        if (totalObservados > 0) {
            labels.push('Votos Observados');
            dataValues.push(totalObservados);
        }

        if (totalAusentes > 0) {
            labels.push('Ausentes (No Votaron)');
            dataValues.push(totalAusentes);
        }

        const sumaTotal = dataValues.reduce((a, b) => a + b, 0);
        if (sumaTotal === 0) return null;

        return {
            labels: labels,
            datasets: [
                {
                    data: dataValues,
                    backgroundColor: [
                        '#2563EB', '#10B981', '#F59E0B', '#8B5CF6',
                        '#6B7280', '#EF4444', '#F97316', '#CBD5E1'
                    ],
                    hoverBackgroundColor: [
                        '#1D4ED8', '#059669', '#D97706', '#7C3AED',
                        '#4B5563', '#DC2626', '#EA580C', '#94A3B8'
                    ]
                }
            ]
        };
    }, [local]);

    const pieChartOptions = {
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    boxWidth: 10,
                    font: { size: 12 }
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const valor = context.parsed || 0;
                        const pct = total > 0 ? ((valor / total) * 100).toFixed(1) : 0;
                        return ` ${context.label}: ${valor.toLocaleString()} votos (${pct}%)`;
                    }
                }
            }
        },
        maintainAspectRatio: false,
        responsive: true
    };

    const accionesMesaTemplate = (rowData) => {
        if (rowData.registrada) {
            return (
                <Button
                    icon="pi pi-eye"
                    label="Ver Cómputo"
                    className="p-button-sm p-button-rounded p-button-outlined p-button-info"
                    onClick={() => { setSelectedMesa(rowData); setDetalleMesaDialog(true); }}
                />
            );
        }
        return (
            <Button
                icon="pi pi-pencil"
                label="Registrar Votos"
                className="p-button-sm p-button-rounded p-button-success"
                onClick={() => abrirRegistroMesa(rowData)}
            />
        );
    };

    // Helper para obtener el logo a partir de la clave o nombre del candidato
    const obtenerLogoPorNombreOCodigo = (candidatoStr) => {
        if (!candidatoStr) return '/logos/otros.png';
        const item = CANDIDATOS_LISTA.find(c =>
            candidatoStr.startsWith(c.codigo) || candidatoStr.includes(c.nombre)
        );
        return item ? item.logo : '/logos/otros.png';
    };

    return (
        <div className="grid p-fluid">
            <Toast ref={toast} />

            {loading && (
                <div className="col-12 flex justify-content-center align-items-center py-8">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                </div>
            )}

            {!loading && !local && (
                <div className="col-12">
                    <div className="card text-center p-6 surface-card shadow-2 border-round-xl border-1 surface-border">
                        <div className="inline-flex align-items-center justify-content-center bg-orange-50 border-circle mb-3" style={{ width: '4.5rem', height: '4.5rem' }}>
                            <i className="pi pi-exclamation-triangle text-orange-500 text-3xl" />
                        </div>
                        <h3 className="text-900 font-bold mb-2">No tienes un local de votación asignado</h3>
                        <p className="text-500 max-w-26rem mx-auto line-height-3 mb-4">
                            No se encontraron centros de votación vinculados a tu cuenta. Contacta con el equipo de soporte electoral de El Porvenir.
                        </p>
                        <Button label="Recargar Vista" icon="pi pi-refresh" className="p-button-outlined p-button-secondary w-auto" onClick={peticionGetLocalAsignado} />
                    </div>
                </div>
            )}

            {!loading && local && (
                <div className="col-12">
                    <div className="card surface-card shadow-2 border-round-xl border-1 surface-border p-4 mb-4"
                        style={{ background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)' }}>
                        <div className="flex flex-column lg:flex-row justify-content-between align-items-start lg:align-items-center gap-3">
                            <div className="flex align-items-center gap-3">
                                <div className="hidden sm:flex align-items-center justify-content-center bg-primary-50 text-primary border-circle shadow-1" style={{ width: '4rem', height: '4rem' }}>
                                    <i className="pi pi-building text-2xl text-primary" />
                                </div>
                                <div>
                                    <div className="flex align-items-center gap-2 mb-1">
                                        <Tag value="CENTRO ASIGNADO" severity="info" className="text-xs tracking-wider" />
                                        <span className="text-500 text-xs font-medium">Distrito El Porvenir</span>
                                    </div>
                                    <h2 className="m-0 text-900 font-bold text-2xl">{local.nombre}</h2>
                                    <p className="text-500 m-0 mt-1 flex align-items-center gap-2 text-sm">
                                        <i className="pi pi-map-marker text-red-500" />
                                        {local.direccion}
                                    </p>
                                </div>
                            </div>

                            <div className="flex flex-wrap align-items-center gap-2 w-full lg:w-auto justify-content-between lg:justify-content-end">
                                <div className="surface-100 border-round-2xl px-3 py-2 flex align-items-center gap-2 border-1 surface-border">
                                    <Button
                                        label="Administrar Mesas"
                                        icon="pi pi-list"
                                        className="p-button-text p-button-secondary"
                                        onClick={() => setMesasDialog(true)}
                                    />
                                </div>
                                <div className="surface-100 border-round-2xl px-3 py-2 flex align-items-center gap-2 border-1 surface-border">
                                    <Avatar icon="pi pi-user" shape="circle" className="bg-primary text-white" />
                                    <div className="text-left">
                                        <span className="block text-400 font-semibold" style={{ fontSize: '0.7rem' }}>RESPONSABLE</span>
                                        <span className="text-800 font-bold text-xs">{user?.nombres || 'Usuario'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <Divider className="my-4" />

                        <div className="grid">
                            <div className="col-12 sm:col-6 lg:col-3">
                                <div className="surface-0 p-3 border-round-xl border-1 surface-border shadow-1 hover:shadow-2 transition-all transition-duration-200">
                                    <div className="flex justify-content-between align-items-center mb-2">
                                        <span className="text-500 font-semibold text-xs uppercase tracking-wide">Mesas Instaladas</span>
                                        <div className="flex align-items-center justify-content-center bg-blue-50 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                            <i className="pi pi-table text-blue-600 text-lg" />
                                        </div>
                                    </div>
                                    <div className="text-900 font-bold text-3xl">{local.cantidadMesas}</div>
                                    <span className="text-500 text-xs mt-1 block">Tope máximo habilitado</span>
                                </div>
                            </div>

                            <div className="col-12 sm:col-6 lg:col-3">
                                <div className="surface-0 p-3 border-round-xl border-1 surface-border shadow-1 hover:shadow-2 transition-all transition-duration-200">
                                    <div className="flex justify-content-between align-items-center mb-2">
                                        <span className="text-500 font-semibold text-xs uppercase tracking-wide">Mesas Computadas</span>
                                        <div className="flex align-items-center justify-content-center bg-green-50 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                            <i className="pi pi-check-circle text-green-600 text-lg" />
                                        </div>
                                    </div>
                                    <div className="text-green-600 font-bold text-3xl">{mesasRegistradasCount}</div>
                                    <span className="text-green-700 text-xs mt-1 block">
                                        {local.cantidadMesas - mesasRegistradasCount} mesas restantes
                                    </span>
                                </div>
                            </div>

                            <div className="col-12 sm:col-6 lg:col-3">
                                <div className="surface-0 p-3 border-round-xl border-1 surface-border shadow-1 hover:shadow-2 transition-all transition-duration-200">
                                    <div className="flex justify-content-between align-items-center mb-2">
                                        <span className="text-500 font-semibold text-xs uppercase tracking-wide">Padrón Electoral</span>
                                        <div className="flex align-items-center justify-content-center bg-purple-50 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                            <i className="pi pi-users text-purple-600 text-lg" />
                                        </div>
                                    </div>
                                    <div className="text-900 font-bold text-3xl">{local.electoresHabiles?.toLocaleString()}</div>
                                    <span className="text-500 text-xs mt-1 block">Electores habilitados</span>
                                </div>
                            </div>

                            <div className="col-12 sm:col-6 lg:col-3">
                                <div className="surface-0 p-3 border-round-xl border-1 surface-border shadow-1 hover:shadow-2 transition-all transition-duration-200">
                                    <div className="flex justify-content-between align-items-center mb-2">
                                        <span className="text-500 font-semibold text-xs uppercase tracking-wide">Avance de Cómputo</span>
                                        <div className="flex align-items-center justify-content-center bg-orange-50 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                            <i className="pi pi-chart-line text-orange-600 text-lg" />
                                        </div>
                                    </div>
                                    <div className="flex align-items-baseline gap-2">
                                        <span className="text-900 font-bold text-3xl">{porcentajeAvance}%</span>
                                        <span className="text-500 text-xs font-medium">procesado</span>
                                    </div>
                                    <ProgressBar
                                        value={porcentajeAvance}
                                        showValue={false}
                                        style={{ height: '8px', borderRadius: '4px' }}
                                        className="mt-2"
                                        color={porcentajeAvance === 100 ? '#22c55e' : '#3b82f6'}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid mt-3">
                            <div className="col-12">
                                <div className="surface-0 p-4 border-round-xl border-1 surface-border shadow-1 flex flex-column justify-content-between">
                                    <div className="flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <span className="text-900 font-bold text-lg block">Cómputo General del Local</span>
                                            <span className="text-500 text-xs">Top 4 partidos, ausentismo e inválidos</span>
                                        </div>
                                        <Tag severity={mesasRegistradasCount > 0 ? 'success' : 'info'} value={`${mesasRegistradasCount}/${local.cantidadMesas} Mesas`} />
                                    </div>

                                    {pieChartData ? (
                                        <div className="flex justify-content-center align-items-center" style={{ minHeight: '320px', position: 'relative' }}>
                                            <Chart type="pie" data={pieChartData} options={pieChartOptions} style={{ width: '100%', maxHeight: '320px' }} />
                                        </div>
                                    ) : (
                                        <div className="text-center py-6 text-500 flex flex-column align-items-center justify-content-center" style={{ minHeight: '320px' }}>
                                            <i className="pi pi-chart-pie text-4xl text-300 mb-2" />
                                            <span>No hay actas computadas para generar el gráfico.</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Principal: Listado de Mesas */}
            <Dialog
                visible={mesasDialog}
                style={{ width: '920px', maxWidth: '95vw' }}
                header={
                    <DialogHeader
                        title={<span style={{ color: 'white' }}>{`Padrón de Mesas — ${local?.nombre}`}</span>}
                        icon="pi pi-table"
                    />
                }
                modal
                className="p-fluid custom-profile-dialog"
                onHide={() => setMesasDialog(false)}
                footer={
                    <div className="flex justify-content-end">
                        <Button label="Cerrar" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={() => setMesasDialog(false)} />
                    </div>
                }
            >
                <div className="pt-2">
                    <DataTable
                        value={local?.mesas || []}
                        paginator
                        rows={6}
                        rowsPerPageOptions={[6, 12, 24]}
                        emptyMessage="No hay mesas registradas para este local."
                        className="p-datatable-sm"
                        stripedRows
                        responsiveLayout="scroll"
                    >
                        <Column
                            field="numeroMesa"
                            header="Mesa"
                            sortable
                            style={{ width: '130px' }}
                            body={(m) => (
                                <span className="font-bold text-800 bg-surface-100 border-round px-2 py-1">
                                    N° {String(m.numeroMesa).padStart(2, '0')}
                                </span>
                            )}
                        />
                        <Column header="Estado" body={estadoMesaTemplate} style={{ width: '160px' }} />
                        <Column field="votosValidos" header="Votos Válidos" body={(m) => <span className="font-semibold text-green-700">{m.votosValidos ?? 0}</span>} />
                        <Column field="votosInvalidos" header="Votos Inválidos" body={(m) => <span className="font-semibold text-red-600">{m.votosInvalidos ?? 0}</span>} />
                        <Column field="noAsistieron" header="Ausentismo" body={(m) => m.noAsistieron ?? 0} />
                        <Column header="Acción" body={accionesMesaTemplate} style={{ width: '160px', textAlign: 'center' }} />
                    </DataTable>
                </div>
            </Dialog>

            {/* Modal Secundario: Detalle del Cómputo de la Mesa con logos en la tabla */}
            <Dialog
                visible={detalleMesaDialog}
                style={{ width: '680px', maxWidth: '90vw' }}
                header={
                    <DialogHeader
                        title={<span style={{ color: 'white' }}>{`Detalle de Escrutinio — Mesa #${selectedMesa?.codigo || ''}`}</span>}
                        icon="pi pi-file-edit"
                    />
                }
                modal
                className="p-fluid custom-profile-dialog"
                onHide={() => setDetalleMesaDialog(false)}
            >
                {selectedMesa && (
                    <div>
                        <div className="grid text-center mb-3">
                            <div className="col-4">
                                <div className="surface-50 border-round-lg p-2 border-1 surface-border">
                                    <span className="text-500 text-xs uppercase font-bold block mb-1">Válidos</span>
                                    <span className="text-xl font-bold text-green-600">{selectedMesa.votosValidos}</span>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="surface-50 border-round-lg p-2 border-1 surface-border">
                                    <span className="text-500 text-xs uppercase font-bold block mb-1">Observados</span>
                                    <span className="text-xl font-bold text-orange-500">{selectedMesa.votosObservados}</span>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="surface-50 border-round-lg p-2 border-1 surface-border">
                                    <span className="text-500 text-xs uppercase font-bold block mb-1">Inválidos</span>
                                    <span className="text-xl font-bold text-red-500">{selectedMesa.votosInvalidos}</span>
                                </div>
                            </div>
                        </div>

                        <Divider align="center">
                            <span className="text-xs uppercase text-500 font-bold tracking-wider">Desglose de Votos por Partido</span>
                        </Divider>

                        <DataTable
                            value={selectedMesa.votosCandidatos || []}
                            emptyMessage="No hay datos de candidatos registrados en esta mesa."
                            className="p-datatable-sm"
                            responsiveLayout="scroll"
                        >
                            <Column
                                field="candidato"
                                header="Organización Política / Candidato"
                                body={(c) => (
                                    <div className="flex align-items-center gap-2">
                                        <img
                                            src={obtenerLogoPorNombreOCodigo(c.candidato)}
                                            alt="Logo Partido"
                                            style={{ width: '28px', height: '28px', objectFit: 'contain' }}
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                            className="border-round shadow-1"
                                        />
                                        <span className="font-semibold text-800">{c.candidato}</span>
                                    </div>
                                )}
                            />
                            <Column
                                field="votos"
                                header="Total Votos"
                                style={{ width: '120px', textAlign: 'right' }}
                                body={(v) => (
                                    <span className="bg-primary-50 text-primary font-bold px-3 py-1 border-round">
                                        {v.votos}
                                    </span>
                                )}
                            />
                        </DataTable>
                    </div>
                )}
            </Dialog>

            {/* Modal de Registro de Escrutinio por Mesa */}
            <Dialog
                visible={registroDialog}
                style={{ width: '1200px', maxWidth: '95vw' }}
                header={
                    <DialogHeader
                        title={<span style={{ color: 'white' }}>{`Escrutinio de Mesa #${mesaEnProceso?.numeroMesa || ''}`}</span>}
                        icon="pi pi-plus"
                    />
                }
                modal
                className="p-fluid custom-profile-dialog"
                onHide={() => setRegistroDialog(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={() => setRegistroDialog(false)} disabled={guardandoVotos} />
                        <Button label="Cerrar y Guardar Acta" icon="pi pi-check" className="p-button-success" onClick={guardarEscrutinio} loading={guardandoVotos} />
                    </div>
                }
            >
                <div className="p-fluid formgrid grid pt-2">
                    <div className="col-12"><h5 className="m-0 text-primary">Identificación de Mesa y Personero</h5></div>
                    <div className="field col-12 md:col-3">
                        <label>Código de Mesa *</label>
                        <InputText
                            value={formVotos.codigoMesa}
                            onChange={(e) => setFormVotos({ ...formVotos, codigoMesa: e.target.value })}
                            placeholder="Ej. 045892"
                            maxLength={20}
                        />
                    </div>
                    <div className="field col-12 md:col-3">
                        <label>DNI Personero *</label>
                        <div className="p-inputgroup">
                            <InputText
                                value={formVotos.personeroDocIdentidad}
                                onChange={(e) => setFormVotos({ ...formVotos, personeroDocIdentidad: e.target.value })}
                                onBlur={buscarPersoneroPorDNI}
                                placeholder="DNI (8 dígitos)"
                                maxLength={8}
                            />
                            <Button icon="pi pi-search" className="p-button-secondary" onClick={buscarPersoneroPorDNI} />
                        </div>
                    </div>
                    <div className="field col-12 md:col-3">
                        <label>Nombres *</label>
                        <InputText
                            value={formVotos.personeroNombre}
                            onChange={(e) => setFormVotos({ ...formVotos, personeroNombre: e.target.value })}
                            placeholder="Nombres completos"
                        />
                    </div>
                    <div className="field col-12 md:col-3">
                        <label>Apellidos *</label>
                        <InputText
                            value={formVotos.personeroApellido}
                            onChange={(e) => setFormVotos({ ...formVotos, personeroApellido: e.target.value })}
                            placeholder="Apellidos completos"
                        />
                    </div>

                    <Divider className="col-12 my-2" />

                    {/* Votos por Candidato con Logo y Nombre */}
                    <div className="col-12"><h5 className="m-0 text-primary">Votos Válidos por Organización Política</h5></div>
                    {CANDIDATOS_LISTA.map((cand) => (
                        <div key={cand.codigo} className="field col-12 md:col-12">
                            <div className="flex align-items-center gap-2 mb-2">
                                <img
                                    src={cand.logo}
                                    alt={cand.codigo}
                                    style={{ width: '28px', height: '28px', objectFit: 'contain' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                    className="border-round shadow-1"
                                />
                                <label className="text-xs font-semibold m-0">{cand.codigo} — {cand.nombre}</label>
                            </div>
                            <InputNumber
                                value={formVotos.votosPorCandidato[cand.codigo]}
                                onValueChange={(e) => setFormVotos({
                                    ...formVotos,
                                    votosPorCandidato: { ...formVotos.votosPorCandidato, [cand.codigo]: e.value || 0 }
                                })}
                                min={0}
                                showButtons
                            />
                        </div>
                    ))}

                    <Divider className="col-12 my-2" />

                    <div className="col-12"><h5 className="m-0 text-primary">Otros Votos y Ausentismo</h5></div>
                    <div className="field col-12 md:col-4">
                        <label>Votos en Blanco / Nulos *</label>
                        <InputNumber
                            value={formVotos.votosInvalidos}
                            onValueChange={(e) => setFormVotos({ ...formVotos, votosInvalidos: e.value || 0 })}
                            min={0}
                            showButtons
                        />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label>Electores No Asistieron *</label>
                        <InputNumber
                            value={formVotos.noAsistieron}
                            onValueChange={(e) => setFormVotos({ ...formVotos, noAsistieron: e.value || 0 })}
                            min={0}
                            showButtons
                        />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label>Votos Impugnados *</label>
                        <InputNumber
                            value={formVotos.votosObservados}
                            onValueChange={(e) => setFormVotos({ ...formVotos, votosObservados: e.value || 0 })}
                            min={0}
                            showButtons
                        />
                    </div>

                    <div className="col-12 mt-3">
                        <div className={`p-3 border-round border-1 ${limiteElectores > 0 && totalGeneral > limiteElectores ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-200'}`}>
                            <div className="flex justify-content-between font-bold text-sm">
                                <span>Votos Válidos: {totalValidos}</span>
                                <span>Total Computado: {totalGeneral}</span>
                                <span>Tope Máximo Local: {limiteElectores}</span>
                            </div>
                            {limiteElectores > 0 && totalGeneral > limiteElectores && (
                                <small className="text-red-600 block mt-1 font-semibold">
                                    ¡Atención! La suma computada ({totalGeneral}) supera los electores hábiles ({limiteElectores}).
                                </small>
                            )}
                        </div>
                    </div>
                </div>
            </Dialog>

        </div>
    );
};